/**
 * Sort vocabulary for the Matter Files table.
 *
 * The drive's three sortable columns are Name, Last Modified and Size, but the two list endpoints
 * that fill the table name those columns differently — folders sort by `name`/`file_count`, files
 * by `original_filename`/`size_bytes`. Declaring the column keys once here, with one map per
 * endpoint, keeps that translation in a single place and keeps display labels out of the query
 * string.
 *
 * Backend contract (`paxton_app/user_files/database/pagination.py`, added in PR #512):
 * - Omitting the parameters is name ascending, which is the drive's default too.
 * - A keyset cursor carries the sort it was issued under and the API rejects reuse under a
 *   different sort with `400 Invalid pagination cursor.`, so every page of one listing must repeat
 *   the same values and a sort change must restart pagination.
 */

/** Sortable drive column. Values double as the TanStack column ids in `FolderList`. */
export type DriveSortKey = 'name' | 'modified' | 'size'

export type DriveSortDirection = 'asc' | 'desc'

export type DriveListSort = {
  key: DriveSortKey
  direction: DriveSortDirection
}

/** Name ascending — what the API returns when the parameters are omitted, and the design's default. */
export const kDefaultDriveListSort: DriveListSort = { key: 'name', direction: 'asc' }

/** `sort_by` values accepted by `GET /folders/{id}/children`. */
export const kFolderChildrenSortByParam: Record<DriveSortKey, string> = {
  name: 'name',
  modified: 'updated_at',
  size: 'file_count',
}

/** `sort_by` values accepted by `GET /folders/{id}/files`. */
export const kFolderFilesSortByParam: Record<DriveSortKey, string> = {
  name: 'original_filename',
  modified: 'updated_at',
  size: 'size_bytes',
}

export function driveListSortsEqual(a: DriveListSort | undefined, b: DriveListSort | undefined): boolean {
  return a?.key === b?.key && a?.direction === b?.direction
}
